import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit, output, QueryList, signal, untracked, ViewChild, ViewChildren } from '@angular/core';
import { NgClass } from '@angular/common';
import { CardComponent } from '../card/card.component';
import { GridsterComponent, GridsterConfig, GridsterItem, GridsterModule } from 'angular-gridster2';
import { CardItem, CardChartConfigurations, CardGridConfigurations } from '@interface/card.interface';
import { AddContentModalComponent } from '../add-content-modal/add-content-modal.component';
import { AddInsightsModalComponent } from '../add-insights-modal/add-insights-modal.component';
import { ConfirmationPopupComponent } from 'src/app/shared/common-ui-components/confirmation-popup/confirmation-popup.component';
import { Subscription } from 'rxjs';
import { CanvasContentService } from '../../canvas-content.service';
import { AssetsApiService } from 'src/app/core/http/services/assets.service';
import { cloneDeep } from 'lodash';

@Component({
  selector: 'app-cards-container',
  standalone: true,
  imports: [NgClass, CardComponent, GridsterModule, AddContentModalComponent, AddInsightsModalComponent, ConfirmationPopupComponent],
  templateUrl: './cards-container.component.html',
  styleUrl: './cards-container.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CardsContainerComponent implements OnInit {
  private subscription: Subscription = new Subscription();
  private canvasContentService = inject(CanvasContentService);
  private assetsApiService = inject(AssetsApiService);

  // Inputs (developer preview)
  isCanvasLocked = input<boolean>(false);
  isDrawerOpen = input<boolean>(false);
  canvasId = input<string>('');

  // Outputs (developer preview)
  selectedCardsChange = output<{ reportIds: string[], answerIds: string[] }>();
  freeFormCardsChange = output<{ cardId: string, assetId: string, content: string }[]>();
  unsavedFreeFormCardsChange = output<{ cardId: string, hasUnsavedChanges: boolean }[]>();
  triggerCanvasSave = output<void>();
  insightsCompleted = output<{ reportName: string, insights: string }>();
  insightsError = output<string>();
  cardRemoved = output<{ reportId?: string, questionId?: string }>();

  @ViewChild(GridsterComponent, { static: false }) gridster!: GridsterComponent;
  @ViewChild('insightsModal', { static: false }) insightsModal!: any;
  @ViewChildren(CardComponent) cardComponents!: QueryList<CardComponent>;

  options: GridsterConfig = {};
  private _gridsterComponent: any;
  private isDragging = false;

  // State as signals
  cards = signal<CardItem[]>([]);
  showAddContentModal = signal<boolean>(false);
  showInsightsModal = signal<boolean>(false);
  addedReportIds = signal<Set<string>>(new Set<string>());
  addedQuestionIds = signal<Set<string>>(new Set<string>());
  selectedCards = signal<Map<string, { reportId?: string, answerId?: string }>>(new Map());
  pendingInsightsReportId = signal<string | null>(null);
  triggerInsightsForReportId = signal<string | null>(null);
  triggerInsightsForQuestionId = signal<string | null>(null);
  sourceCardId = signal<string | null>(null);
  showConfirmationPopup = signal<boolean>(false);
  confirmationPopupMsg = signal<string>('');
  confirmationPopupHeader = signal<string>('');
  confirmationCallbacks: any = {
    confirmAction: () => this.closeConfirmationPopUp(),
    cancelAction: () => this.closeConfirmationPopUp()
  };

  // Non-reactive Map (not displayed in template directly)
  freeFormCards: Map<string, { assetId: string, content: string }> = new Map();
  // Reactive signal so computed() re-evaluates whenever entries are added/removed
  unsavedFreeFormCards = signal<Map<string, boolean>>(new Map());

  hasUnsavedFreeFormCards = computed(() => this.unsavedFreeFormCards().size > 0);

  constructor() {
    // Trigger gridster resize when the side-drawer open state changes
    effect(() => {
      const _ = this.isDrawerOpen();
      untracked(() => {
        if (this.gridster) {
          setTimeout(() => window.dispatchEvent(new Event('resize')), 350);
        }
      });
    });
  }

  ngOnInit(): void {
    this.initializeGridster();
  }

  initializeGridster(): void {
    this.options = {
      gridType: 'scrollVertical',
      displayGrid: 'always',
      fixedRowHeight: 200,
      margin: 10,
      outerMargin: true,
      outerMarginTop: null,
      outerMarginRight: null,
      outerMarginBottom: null,
      outerMarginLeft: null,
      mobileBreakpoint: 640,
      minCols: 12,
      maxCols: 12,
      minRows: 1,
      maxRows: 100,
      maxItemCols: 12,
      minItemCols: 4,
      maxItemRows: 10,
      minItemRows: 2,
      minItemArea: 4,
      defaultItemCols: 4,
      defaultItemRows: 3,
      draggable: { enabled: true },
      resizable: { enabled: true },
      swap: false,
      pushItems: true,
      disablePushOnDrag: false,
      disablePushOnResize: false,
      pushDirections: { north: true, east: true, south: true, west: true },
      pushResizeItems: true,
      disableWindowResize: false,
      disableWarnings: false,
      scrollToNewItems: false,
      itemChangeCallback: this.onItemChange.bind(this),
      itemResizeCallback: this.onItemResize.bind(this),
    };
  }

  removeCard(id: string): void {
    const cardToRemove = this.cards().find(card => card.id === id);

    if (cardToRemove) {
      if (cardToRemove.reportId) {
        this.addedReportIds.update(set => {
          const s = new Set(set);
          s.delete(cardToRemove.reportId!);
          return s;
        });
      }
      if (cardToRemove.questionId) {
        this.addedQuestionIds.update(set => {
          const s = new Set(set);
          s.delete(cardToRemove.questionId!);
          return s;
        });
      }

      if (cardToRemove.cardType === 'FREEFORM_CARD') {
        this.freeFormCards.delete(id);
        this.unsavedFreeFormCards.update(m => { const n = new Map(m); n.delete(id); return n; });
        this.emitFreeFormCardsChange();
        this.emitUnsavedFreeFormCardsChange();
      }

      this.cards.update(cards => cards.filter(card => card.id !== id));
      this.cardRemoved.emit({
        reportId: cardToRemove.reportId,
        questionId: cardToRemove.questionId
      });
    }
  }

  updateCardAnswerId(event: { cardId: string, answerId: string }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);

    if (cardIndex !== -1 && !this.cards()[cardIndex].answerId) {
      this.cards.update(cards => {
        const updated = [...cards];
        updated[cardIndex] = { ...updated[cardIndex], answerId: event.answerId };
        return updated;
      });

      const currentSelectedCards = this.selectedCards();
      if (currentSelectedCards.has(event.cardId)) {
        const cardData = currentSelectedCards.get(event.cardId);
        this.selectedCards.update(map => {
          const m = new Map(map);
          m.set(event.cardId, { ...cardData, answerId: event.answerId });
          return m;
        });
        this.emitSelectedCards();
      }
    }
  }

  onCardSelectionChange(event: { cardId: string, isSelected: boolean, reportId: string, answerId: string }): void {
    if (event.isSelected) {
      this.selectedCards.update(map => {
        const m = new Map(map);
        m.set(event.cardId, { reportId: event.reportId, answerId: event.answerId });
        return m;
      });
    } else {
      this.selectedCards.update(map => {
        const m = new Map(map);
        m.delete(event.cardId);
        return m;
      });
    }
    this.emitSelectedCards();
  }

  private emitSelectedCards(): void {
    const reportIds: string[] = [];
    const answerIds: string[] = [];

    this.selectedCards().forEach(card => {
      if (card.reportId) reportIds.push(card.reportId);
      if (card.answerId) answerIds.push(card.answerId);
    });

    this.selectedCardsChange.emit({ reportIds, answerIds });
  }

  onItemChange(item: any, itemComponent: any): void {
    // Item change callback for gridster
  }

  onItemResize(item: any, itemComponent: any): void {
    if (this.gridster) {
      setTimeout(() => {
        this.gridster.checkCollision(item);
      }, 0);
    }
  }

  closeAddContentModal(): void {
    this.showAddContentModal.set(false);
  }

  openInsightsModal(cardId: string): void {
    this.sourceCardId.set(cardId);
    this.showInsightsModal.set(true);
  }

  closeInsightsModal(): void {
    this.showInsightsModal.set(false);
  }

  handleInsightsSelection(selectedInsight: any): void {
    if (!selectedInsight) {
      console.error('No insight selected');
      return;
    }

    if (selectedInsight.type === 'GENERATE_INSIGHTS') {
      this.generateInsightsFromExistingReportCard(selectedInsight.reportId);
      return;
    }

    if (selectedInsight.type === 'ANSWER') {
      this.generateInsightsFromExistingAnswerCard(selectedInsight.questionId);
      return;
    }
  }

  handleAddToCanvas(event: any): void {
    if (!event?.items?.length) {
      console.error('No valid items received for adding to canvas');
      return;
    }

    event.items.filter((item: any) => !item?.isAdded).forEach((item: any, index: number) => {
      const cardId = `card-${Date.now()}-${Math.floor(Math.random() * 1000)}-${index}`;
      const nextPosition = this.findNextAvailablePosition();

      const newCard: CardItem = {
        id: cardId,
        x: nextPosition.x,
        y: nextPosition.y,
        cols: 4,
        rows: 3,
        title: item.title || 'Untitled Card',
      };

      if (item.type === 'REPORT') {
        newCard.reportId = item.reportId;
        if (item.reportId) {
          this.addedReportIds.update(s => new Set([...s, item.reportId]));
        }
        newCard.cardType = 'REPORT';
      } else if (item.type === 'ANSWER') {
        newCard.questionId = item.questionId;
        newCard.conversationId = item.conversationId;
        newCard.questionText = item.questionText;
        newCard.cardType = 'ANSWER';
        if (item?.questionId) {
          this.addedQuestionIds.update(s => new Set([...s, item.questionId]));
        }
      } else if (item.type === 'FREEFORM_CARD') {
        newCard.cardType = 'FREEFORM_CARD';
        newCard.title = 'Free Form Card';
      }

      this.cards.update(cards => [...cards, { ...newCard }]);
    });
  }

  private findNextAvailablePosition(): { x: number, y: number } {
    const currentCards = this.cards();
    if (currentCards.length === 0) {
      return { x: 0, y: 0 };
    }

    let maxY = 0;
    let maxYCard: CardItem | null = null;

    for (const card of currentCards) {
      if (card.y + card.rows > maxY) {
        maxY = card.y + card.rows;
        maxYCard = card;
      } else if (card.y + card.rows === maxY && card.x > (maxYCard?.x || 0)) {
        maxYCard = card;
      }
    }

    if (!maxYCard) return { x: 0, y: 0 };

    const lastRowY = maxYCard.y;
    const lastRowCards = currentCards.filter(card => card.y === lastRowY);

    let rightmostX = 0;
    for (const card of lastRowCards) {
      const cardRightEdge = card.x + card.cols;
      if (cardRightEdge > rightmostX) {
        rightmostX = cardRightEdge;
      }
    }

    if (rightmostX + 4 <= 12) {
      return { x: rightmostX, y: lastRowY };
    } else {
      return { x: 0, y: lastRowY + Math.max(...lastRowCards.map(c => c.rows)) };
    }
  }

  updateAllCardPositions(): CardItem[] {
    const latestChartConfigs = this.getLatestChartConfigurationsMap();
    const latestGridConfigs = this.getLatestGridConfigurationsMap();

    return this.cards().map(card => {
      const gridItem = this.gridster.grid.find((item: any) => item.item.id === card.id);
      const latestChartConfig = latestChartConfigs.get(card.id);
      const latestGridConfig = latestGridConfigs.get(card.id);

      let updatedCard = gridItem ? {
        ...card,
        x: gridItem.$item.x,
        y: gridItem.$item.y,
        cols: gridItem.$item.cols,
        rows: gridItem.$item.rows
      } : { ...card };

      if (latestChartConfig) updatedCard.chartConfigurations = latestChartConfig;
      if (latestGridConfig) updatedCard.gridConfigurations = latestGridConfig;

      return updatedCard;
    });
  }

  private getLatestChartConfigurationsMap(): Map<string, CardChartConfigurations> {
    const configMap = new Map<string, CardChartConfigurations>();
    if (this.cardComponents) {
      this.cardComponents.forEach(cardComponent => {
        const latestChartConfig = cardComponent.getLatestChartConfigurations();
        if (latestChartConfig) {
          configMap.set(cardComponent.id(), latestChartConfig);
        }
      });
    }
    return configMap;
  }

  private getLatestGridConfigurationsMap(): Map<string, CardGridConfigurations> {
    const configMap = new Map<string, CardGridConfigurations>();
    if (this.cardComponents) {
      this.cardComponents.forEach(cardComponent => {
        const latestGridConfig = cardComponent.getLatestGridConfigurations();
        if (latestGridConfig) {
          configMap.set(cardComponent.id(), latestGridConfig);
        }
      });
    }
    return configMap;
  }

  onFreeFormAssetCreated(event: { cardId: string, assetId: string, content: string }): void {
    this.freeFormCards.set(event.cardId, { assetId: event.assetId, content: event.content });
    this.unsavedFreeFormCards.update(m => { const n = new Map(m); n.delete(event.cardId); return n; });
    this.emitFreeFormCardsChange();
    this.emitUnsavedFreeFormCardsChange();
  }

  onFreeFormSaveStateChange(event: { cardId: string, hasUnsavedChanges: boolean }): void {
    if (event.hasUnsavedChanges) {
      this.unsavedFreeFormCards.update(m => new Map(m).set(event.cardId, true));
    } else {
      this.unsavedFreeFormCards.update(m => { const n = new Map(m); n.delete(event.cardId); return n; });
    }
    this.emitUnsavedFreeFormCardsChange();
  }

  onTriggerCanvasSave(): void {
    this.triggerCanvasSave.emit();
  }

  onCardTitleChanged(event: { cardId: string, newTitle: string }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);
    if (cardIndex !== -1) {
      this.cards.update(cards => {
        const updated = [...cards];
        if (updated[cardIndex].cardType !== 'FREEFORM_CARD') {
          updated[cardIndex] = { ...updated[cardIndex], title: event.newTitle };
        } else {
          updated[cardIndex].title = event.newTitle;
        }
        return updated;
      });
    }
  }

  onCardDataLoaded(event: { cardId: string, data: any }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);
    if (cardIndex !== -1) {
      this.cards.update(cards => {
        const updated = [...cards];
        updated[cardIndex] = { ...updated[cardIndex], data: event.data };
        return updated;
      });
    }
  }

  onVisualTypeChanged(event: { cardId: string, visualType: 'TABLE' | 'CHART' }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);
    if (cardIndex !== -1) {
      this.cards.update(cards => {
        const updated = [...cards];
        updated[cardIndex].selectedVisualType = event.visualType;
        return updated;
      });
    }
  }

  onChartConfigurationsChanged(event: { cardId: string, chartConfigurations: CardChartConfigurations }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);
    if (cardIndex !== -1) {
      this.cards.update(cards => {
        const updated = [...cards];
        updated[cardIndex].chartConfigurations = event.chartConfigurations;
        return updated;
      });
    }
  }

  onGridConfigurationsChanged(event: { cardId: string, gridConfigurations: CardGridConfigurations }): void {
    const cardIndex = this.cards().findIndex(card => card.id === event.cardId);
    if (cardIndex !== -1) {
      this.cards.update(cards => {
        const updated = [...cards];
        updated[cardIndex].gridConfigurations = event.gridConfigurations;
        return updated;
      });
    }
  }

  onDeleteFreeFormCard(event: { cardId: string, assetId: string }): void {
    this.showConfirmation(
      'Deleting the free-form card will delete it permanently. Are you sure?',
      'Delete Free-Form Card',
      () => this.deleteFreeFormCard(event)
    );
  }

  deleteFreeFormCard(event: { cardId: string, assetId: string }): void {
    if (event.assetId) {
      this.assetsApiService.deleteAsset(event.assetId).subscribe({
        next: () => { this.removeCard(event.cardId); },
        error: (error) => {
          console.error('Error deleting free-form card asset:', error);
          this.removeCard(event.cardId);
        }
      });
    } else {
      this.removeCard(event.cardId);
    }
  }

  showConfirmation(message: string, header: string, confirmAction?: () => void): void {
    this.confirmationPopupMsg.set(message);
    this.confirmationPopupHeader.set(header || 'Confirmation');
    this.confirmationCallbacks.confirmAction = confirmAction
      ? () => { confirmAction(); this.closeConfirmationPopUp(); }
      : () => this.closeConfirmationPopUp();
    this.showConfirmationPopup.set(true);
  }

  closeConfirmationPopUp(): void {
    this.showConfirmationPopup.set(false);
    this.confirmationPopupMsg.set('');
    this.confirmationPopupHeader.set('');
  }

  private emitFreeFormCardsChange(): void {
    const freeFormCardsArray: { cardId: string, assetId: string, content: string }[] = [];
    this.freeFormCards.forEach((value, cardId) => {
      freeFormCardsArray.push({ cardId, assetId: value.assetId, content: value.content });
    });
    this.freeFormCardsChange.emit(freeFormCardsArray);
  }

  private emitUnsavedFreeFormCardsChange(): void {
    const unsavedCardsArray: { cardId: string, hasUnsavedChanges: boolean }[] = [];
    this.unsavedFreeFormCards().forEach((hasUnsavedChanges, cardId) => {
      unsavedCardsArray.push({ cardId, hasUnsavedChanges });
    });
    this.unsavedFreeFormCardsChange.emit(unsavedCardsArray);
  }

  generateInsightsFromExistingReportCard(reportId: string): void {
    const reportCard = this.cards().find(card => card.cardType === 'REPORT' && card.reportId === reportId);

    if (!reportCard) {
      console.error('Report card not found in canvas for ID:', reportId);
      return;
    }

    this.pendingInsightsReportId.set(reportId);
    this.triggerInsightsForReportId.set(reportId);

    setTimeout(() => {
      this.triggerInsightsForReportId.set(null);
    }, 1000);
  }

  generateInsightsFromExistingAnswerCard(questionId: string): void {
    const answerCard = this.cards().find(card => card.cardType === 'ANSWER' && card.questionId === questionId);

    if (!answerCard) {
      console.error('Answer card not found in canvas for question ID:', questionId);
      return;
    }

    this.triggerInsightsForQuestionId.set(questionId);

    setTimeout(() => {
      this.triggerInsightsForQuestionId.set(null);
    }, 1000);
  }

  onInsightsGeneratedFromCard(event: { reportName: string, insights: string }): void {
    const currentSourceCardId = this.sourceCardId();
    if (currentSourceCardId) {
      const sourceCard = this.cards().find(card => card.id === currentSourceCardId);
      if (sourceCard?.cardType === 'FREEFORM_CARD') {
        const cardComponent = this.cardComponents.find(card => card.id() === currentSourceCardId);
        if (cardComponent?.freeFormEditorComp) {
          cardComponent.freeFormEditorComp.appendInsights(event.reportName, event.insights);
        } else {
          console.error('Could not find free-form editor component to append insights');
        }
      }
    }

    if (this.insightsModal) {
      this.insightsModal.onInsightsCompleted();
    }

    this.insightsCompleted.emit({ reportName: event.reportName, insights: event.insights });
    this.pendingInsightsReportId.set(null);
    this.sourceCardId.set(null);
  }

  onInsightsError(error: string): void {
    console.error('Insights generation failed:', error);

    if (this.insightsModal) {
      this.insightsModal.onInsightsError(error);
    }

    this.insightsError.emit(error);
    this.pendingInsightsReportId.set(null);
  }

  get freeFormCardsData(): { cardId: string, assetId: string, content: string }[] {
    const result: { cardId: string, assetId: string, content: string }[] = [];
    this.freeFormCards.forEach((value, cardId) => {
      result.push({ cardId, assetId: value.assetId, content: value.content });
    });
    return result;
  }
}
